import { Text, View, ScrollView, Pressable, Linking, RefreshControl, TouchableOpacity, useWindowDimensions, Share, InteractionManager } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TabView } from 'react-native-tab-view';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Toast from 'react-native-toast-message';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { LegendList } from '@legendapp/list';
import Hyperlink from 'react-native-hyperlink';
import MapView, { Marker } from 'react-native-maps';

import { cancelEventInstance, deleteEventInstance, addEventRegistrant, removeEventRegistrant, addAttendance, removeAttendanceFromExtensionSet, resolveCheckinTarget, resolvePendingRegistrant, deletePendingEventRegistrant } from '@/data/events';
import { createTaskInstance, deleteTaskInstance, applyAssignmentTemplate } from '@/data/tasks';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ServiceFlowExporter from '@/utils/ServiceFlowExporter';
import ServiceFlowInstancePanel from '@/components/Events/ServiceFlow/ServiceFlowInstancePanel';
import { isServiceFlowV2Available } from '@/config/serviceFlows';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { saveToRecents } from '@/utils/recentlyVisited';
import { formatTimeWithTimezone, safeNewDate } from '@/utils/timezone';
import Unicon from '@/components/General/Unicon';
import ProfileCard from '@/components/People/ProfileCard';
import TaskCard from '@/components/Tasks/TaskCard';
import SongCard from '@/components/Music/SongCard';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SheetModal from '@/components/General/SheetModal';
import DropdownMenu from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import LinkPreview from '@/components/Library/LinkPreview';
import ActionButton from '@/components/General/ActionButton';
import IconButton from '@/components/General/IconButton';
import * as Loaders from '@/utils/constants/skeletons';
import * as Sentry from '@sentry/react-native';
import ReachTabBar from '@/components/General/ReachTabBar';
import SelectInput from '@/components/General/inputs/SelectInput.js';
import Checkbox from '@/components/General/inputs/Checkbox';
import PageTitle from '@/components/General/PageTitle';
import Calendar from '@/components/Events/Calendar';
import RegistrantCodesSheet from '@/components/Events/RegistrantCodesSheet';
import RegistrantPathsSheet from '@/components/Events/RegistrantPathsSheet';
import ExtendRegistrationModal from '@/components/Events/ExtendRegistrationModal';
import RegistrantDetailModal from '@/components/Events/RegistrantDetailModal';
import RegistrationEmailModal from '@/components/Events/RegistrationEmailModal';
import useTeamFilterStore from '@/data/store/useTeamFilterStore';


const EventInstanceScreen = () => {
    const { eventInstanceID, tab } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();


    const teamFilter = useTeamFilterStore((s) => s.selectedTeamID);
    useEffect(() => {
        useTeamFilterStore.getState().hydrate();
    }, []);
    const [mapReset, setMapReset] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [wideTabIndex, setWideTabIndex] = useState(0);
    const [registrantSearch, setRegistrantSearch] = useState('');
    const [registrantSort, setRegistrantSort] = useState('registeredAt-desc');
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [selectedTemplateTeamID, setSelectedTemplateTeamID] = useState(null);
    const [manageTasksTeam, setManageTasksTeam] = useState(null);
    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
    const [addTaskSelectedTeamID, setAddTaskSelectedTeamID] = useState(null);
    const [addTaskSelectedTaskIDs, setAddTaskSelectedTaskIDs] = useState([]);
    const [codesSheetRegistrant, setCodesSheetRegistrant] = useState(null);
    const [pathsSheetOpen, setPathsSheetOpen] = useState(false);
    const [extendModalOpen, setExtendModalOpen] = useState(false);
    const [detailModalRegistrant, setDetailModalRegistrant] = useState(null);
    // Group registration flow: registerPickerValue is the SheetSelector's
    // controlled selection (kept if the email modal is cancelled so the picker
    // reopens with the same people). pendingRegistrationPeople non-null opens
    // the email-recipient modal; registration fires on its confirm.
    const [registerPickerValue, setRegisterPickerValue] = useState([]);
    const [pendingRegistrationPeople, setPendingRegistrationPeople] = useState(null);
    const { isNative, isWeb, isIos, isAndroidMapsEnabled } = usePlatform();
    const isWideScreen = width >= 900;

    // ============ QUERIES ============
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID, options: { enabled: !!loggedInUser?.organizationID } });
    const { data: event, isFetching: fetchingEvents, isLoading } = Query.eventInstances.byId({ id: eventInstanceID });
    const { data: siblingInstances = [] } = Query.eventInstances.byEventIdAll({ eventID: event?.eventID, options: { enabled: !!event?.eventID } });
    const availableVolunteerTasks = Query.tasks.byDueType({ dueType: "EVENT_VOLUNTEER" });
    const { data: taskTemplates } = Query.taskAssignmentTemplates.all();
    const { data: volunteerTeams = [] } = Query.taskTeams.all();

    // Track recently visited
    useEffect(() => {
        if (event?.id) {
            const startDate = safeNewDate(event?.startTime) || safeNewDate(event?.Event?.defaultStartTime);
            const subtitle = startDate
                ? startDate.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: organization?.timezone })
                : null;
            saveToRecents({
                id: event.id,
                url: `/events/instances/${event.id}`,
                title: event.nameOverride || event.Event?.name,
                params: { eventInstanceID: event.id },
                subtitle,
            });
        }
    }, [event?.id, event?.startTime, event?.Event?.defaultStartTime, event?.nameOverride, event?.Event?.name, organization?.timezone]);
    // Legacy rows query — retained for tab visibility + header Export; the tab
    // itself renders ServiceFlowInstancePanel (v2 snapshot) or a read-only v1
    // view inside the panel.
    const { data: serviceFlowItems = [], isFetching: fetchingServiceFlow } = Query.serviceFlows.byInstance({ eventInstanceID, options: { enabled: !!eventInstanceID } });
    const { data: extensionSource } = Query.eventInstances.registrationExtensionSource({ eventInstanceID, options: { enabled: !!eventInstanceID } });

    // When the viewed instance is a registration EXTENSION of another instance,
    // all registration data (registrants, paths, pending, codes) belongs to the
    // MAIN registration instance — but check-ins still land on this instance.
    const isRegistrationExtension = !!extensionSource?.mainEventInstanceID;
    const registrationInstanceID = extensionSource?.mainEventInstanceID || eventInstanceID;
    const showRegistration = !!event?.registrationEnabled || isRegistrationExtension;

    const { data: registrants = [], isFetching: fetchingRegistrants, refetch: refetchRegistrants } = Query.eventInstances.registrants({ eventInstanceID: registrationInstanceID, options: { enabled: showRegistration } });
    const { data: registrantPaths = [] } = Query.eventInstances.registrantPaths({ eventInstanceID: registrationInstanceID, options: { enabled: showRegistration } });
    const { data: pendingRegistrants = [] } = Query.eventInstances.pendingRegistrants({ eventInstanceID: registrationInstanceID, options: { enabled: showRegistration } });
    const { data: registrationExtensions = [] } = Query.eventInstances.registrationExtensions({ eventInstanceID: registrationInstanceID, options: { enabled: showRegistration } });

    // Filter and sort registrants
    const filteredAndSortedRegistrants = useMemo(() => {
        let result = [...registrants];

        // Filter by search
        if (registrantSearch) {
            const searchLower = registrantSearch.toLowerCase();
            result = result.filter(r => {
                const fullName = `${r.Person?.firstName || ''} ${r.Person?.lastName || ''}`.toLowerCase();
                return fullName.includes(searchLower);
            });
        }

        // Sort
        const [sortField, sortDirection] = registrantSort.split('-');
        if (sortField === 'registeredAt') {
            // Build a map of groupID -> earliest registeredAt so all group members sort together
            const groupAnchor = new Map();
            result.forEach((item) => {
                const key = item.groupID || `solo-${item.personID}`;
                const t = item.registeredAt ? new Date(item.registeredAt).getTime() : 0;
                if (!groupAnchor.has(key) || t < groupAnchor.get(key)) {
                    groupAnchor.set(key, t);
                }
            });
            result.sort((a, b) => {
                const aKey = a.groupID || `solo-${a.personID}`;
                const bKey = b.groupID || `solo-${b.personID}`;
                const aAnchor = groupAnchor.get(aKey) || 0;
                const bAnchor = groupAnchor.get(bKey) || 0;
                if (aAnchor !== bAnchor) {
                    return sortDirection === 'asc' ? aAnchor - bAnchor : bAnchor - aAnchor;
                }
                // Within the same group, sort by individual registeredAt asc
                return new Date(a.registeredAt || 0) - new Date(b.registeredAt || 0);
            });
        } else {
            result.sort((a, b) => {
                if (sortField === 'name') {
                    const aName = `${a.Person?.firstName || ''} ${a.Person?.lastName || ''}`;
                    const bName = `${b.Person?.firstName || ''} ${b.Person?.lastName || ''}`;
                    return sortDirection === 'asc'
                        ? aName.localeCompare(bName)
                        : bName.localeCompare(aName);
                }
                return 0;
            });
        }

        // Inject registration group headers when sorting by registeredAt
        if (sortField === 'registeredAt') {
            const withHeaders = [];
            let lastGroupKey = undefined;
            result.forEach((item) => {
                const groupKey = item.groupID || `solo-${item.personID}`;
                if (groupKey !== lastGroupKey) {
                    const registeredBy = item.RegisteredBy
                        ? `${item.RegisteredBy.firstName || ''} ${item.RegisteredBy.lastName || ''}`.trim()
                        : null;
                    const timeLabel = item.registeredAt
                        ? new Date(item.registeredAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : 'Unknown time';
                    const sourceLabel = item.source === 'webhook' ? 'Form submission' : item.source === 'qr' ? 'QR scan' : 'Manually registered';
                    const label = registeredBy ? `${registeredBy} · ${timeLabel}` : `${sourceLabel} · ${timeLabel}`;
                    withHeaders.push({ _type: 'groupHeader', label, key: `header-${groupKey}`, groupID: item.groupID });
                    lastGroupKey = groupKey;
                }
                withHeaders.push(item);
            });
            return withHeaders;
        }

        return result;
    }, [registrants, registrantSearch, registrantSort]);

    // ============ MUTATIONS ============
    const createTaskInstanceMutation = useMutation({
        mutationFn: createTaskInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
            if (event?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(event.eventID) });
            }
        }
    });

    const deleteTaskInstanceMutation = useMutation({
        mutationFn: deleteTaskInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
            if (event?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(event.eventID) });
            }
            Toast.show({ type: 'success', text2: 'The task was removed successfully.' });
        }
    });

    const applyTemplateMutation = useMutation({
        mutationFn: applyAssignmentTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) });
            if (event?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(event.eventID) });
            }
            Toast.show({ type: 'success', text2: 'Template applied successfully.' });
            setTemplateModalOpen(false);
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to apply template.' });
        }
    });

    const deleteEventMutation = useMutation({
        mutationFn: deleteEventInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.events.all() });
            router.canGoBack() ? router.back() : router.replace("/events")
        }
    });

    const cancelEventMutation = useMutation({
        mutationFn: cancelEventInstance,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) })
    });

    const addRegistrantMutation = useMutation({
        mutationFn: addEventRegistrant,
        onSuccess: (data, vars) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrants(registrationInstanceID) });
            const results = data?.results || [];
            const registeredCount = results.length > 0
                ? results.filter(r => r.status === 'registered').length
                : (vars?.personIDs?.length || 0);
            const alreadyCount = results.filter(r => r.status === 'alreadyRegistered').length;
            let text;
            if (registeredCount === 0 && alreadyCount > 0) {
                text = alreadyCount === 1 ? 'That person is already registered.' : `${alreadyCount} people were already registered.`;
            } else {
                text = `${registeredCount} ${registeredCount === 1 ? 'person' : 'people'} registered`;
                if (alreadyCount > 0) text += ` (${alreadyCount} already registered)`;
                text += '.';
            }
            if (data?.emailSent) {
                const recipient = pendingRegistrationPeople?.find(p => p.id === vars?.emailRecipientPersonID);
                const recipientName = recipient ? `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim() : null;
                text += recipientName ? ` Confirmation sent to ${recipientName}.` : ' Confirmation email sent.';
            }
            Toast.show({ type: 'success', text2: text });
            setPendingRegistrationPeople(null);
            setRegisterPickerValue([]);
        }
    });

    const removeRegistrantMutation = useMutation({
        mutationFn: removeEventRegistrant,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrants(registrationInstanceID) })
    });

    const [pendingLinkPerson, setPendingLinkPerson] = useState({});

    const resolvePendingMutation = useMutation({
        mutationFn: resolvePendingRegistrant,
        onSuccess: ({ pendingID }) => {
            queryClient.setQueryData(
                QueryKeys.eventInstances.pendingRegistrants(registrationInstanceID),
                (old) => old?.filter(p => p.id !== pendingID) ?? []
            );
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrants(registrationInstanceID) });
            Toast.show({ type: 'success', text2: 'Registrant resolved successfully.' });
        },
        onError: (error) => {
            // Stale pending rows (resolved/removed elsewhere) should disappear
            // from the list once we refresh.
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.pendingRegistrants(registrationInstanceID) });
            Toast.show({
                type: 'error',
                text2: error?.message === 'Pending record not found'
                    ? 'This pending registration was already resolved or removed.'
                    : 'Failed to resolve registrant.'
            });
        },
    });

    const deletePendingMutation = useMutation({
        mutationFn: deletePendingEventRegistrant,
        onSuccess: ({ pendingID }) => {
            queryClient.setQueryData(
                QueryKeys.eventInstances.pendingRegistrants(registrationInstanceID),
                (old) => old?.filter(p => p.id !== pendingID) ?? []
            );
            Toast.show({ type: 'success', text2: 'Pending registration removed.' });
        },
        onError: () => Toast.show({ type: 'error', text2: 'Failed to remove pending registration.' }),
    });

    const checkInRegistrantMutation = useMutation({
        mutationFn: async (vars) => {
            // Resolve the target instance via the extension set (same RPC the
            // QR scan edge function uses). Falls back to the current instance.
            const target = await resolveCheckinTarget(vars.eventInstanceID);
            const targetEventInstanceID = target?.targetEventInstanceID || vars.eventInstanceID;
            return addAttendance({ ...vars, eventInstanceID: targetEventInstanceID });
        },
        onSuccess: (_, vars) => {
            // Update registrants query data with isCheckedIn property
            queryClient.setQueryData(
                QueryKeys.eventInstances.registrants(registrationInstanceID),
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(r =>
                        r.personID === vars.personID ? { ...r, isCheckedIn: true } : r
                    );
                }
            );
            Toast.show({ type: 'success', text2: 'Person checked in successfully.' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to check in. They may already be checked in.' });
        }
    });

    const removeCheckInMutation = useMutation({
        // Displayed isCheckedIn is set-wide (main + extensions), so removal must
        // clear attendance across the whole set via the main registration ID.
        mutationFn: (vars) => removeAttendanceFromExtensionSet({ ...vars, mainEventInstanceID: registrationInstanceID }),
        onSuccess: (_, vars) => {
            queryClient.setQueryData(
                QueryKeys.eventInstances.registrants(registrationInstanceID),
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(r =>
                        r.personID === vars.attendeeID ? { ...r, isCheckedIn: false } : r
                    );
                }
            );
            Toast.show({ type: 'success', text2: 'Check-in removed.' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to remove check-in.' });
        }
    });

    // ============ DERIVED DATA ============
    const startDate = safeNewDate(event?.startTime);
    const endDate = safeNewDate(event?.endTime);
    const isMultiDay = startDate && endDate && startDate.toDateString() !== endDate.toDateString();
    const locationWithAddress = event?.Locations?.find(l => l.Address && l.Address?.id);
    const sortedLocations = useMemo(() =>
        [...(event?.Locations || [])].sort((a, b) => (a?.name || a?.detailedArea || '').localeCompare(b?.name || b?.detailedArea || '')),
        [event?.Locations]
    );
    const destination = locationWithAddress ? encodeURIComponent(`${locationWithAddress?.Address?.street} ${locationWithAddress?.Address?.zip}, ${locationWithAddress?.Address?.city}`) : null;
    const google_maps_url = isNative ? `comgooglemaps://?daddr=${destination}` : `https://www.google.com/maps/search/?api=1&query=${destination}`;
    const apple_maps_url = isNative ? `maps://?daddr=${destination}` : `https://www.google.com/maps/search/?api=1&query=${destination}`;


    const eventNotes = event?.notes || event?.Event?.defaultNotes || null;
    const eventSpeakers = useMemo(() => {
        if (event?.speakers?.length > 0) return event.speakers;
        if (event?.Event?.defaultSpeaker) return [event.Event.defaultSpeaker];
        return [];
    }, [event]);

    const formattedDate = useMemo(() => {
        if (!event) return '';
        const start = safeNewDate(event?.startTime);
        const end = safeNewDate(event?.endTime);
        const defaultStart = safeNewDate(event?.Event?.defaultStartTime);
        if (isMultiDay && start && end) {
            return start.toLocaleString('default', { month: "short", day: "2-digit", timeZone: organization?.timezone }) +
                " - " + end.toLocaleString('default', { month: "short", day: "2-digit", year: "numeric", timeZone: organization?.timezone });
        }
        const dateToFormat = start || defaultStart;
        return dateToFormat ? dateToFormat.toLocaleString('default', { weekday: "short", month: "short", day: "2-digit", year: "numeric", timeZone: organization?.timezone }) : '';
    }, [event, isMultiDay, organization?.timezone]);

    const formattedTime = useMemo(() => {
        if (!event || isMultiDay) return '';
        const startDate = safeNewDate(event?.startTime) || safeNewDate(event?.Event?.defaultStartTime);
        const endDate = safeNewDate(event?.endTime) || safeNewDate(event?.Event?.defaultEndTime);
        const start = startDate ? formatTimeWithTimezone(startDate, organization?.timezone) : '';
        const end = endDate ? formatTimeWithTimezone(endDate, organization?.timezone) : null;
        return end ? `${start} - ${end}` : start;
    }, [event, isMultiDay, organization?.timezone]);

    const teamFilterOptions = useMemo(() => {
        const options = [{ label: 'All Teams', value: null }];
        const teams = new Set();
        event?.TaskInstances?.filter(t => t?.Task?.dueType === "EVENT_VOLUNTEER").forEach(t => {
            const team = t?.VolunteerTeam || t?.Task?.VolunteerTeamTasks?.[0]?.VolunteerTeam;
            if (team?.id) {
                teams.add(JSON.stringify({ label: team.name, value: team.id }));
            }
        });
        teams.forEach(t => options.push(JSON.parse(t)));
        return options.sort((a, b) => a.label.localeCompare(b.label));
    }, [event?.TaskInstances]);

    // If the persisted team filter isn't represented on this event instance
    // (team has no tasks here), don't surface the raw id in the dropdown —
    // fall back to "All Teams" locally without mutating the store.
    const effectiveTeamFilter = (teamFilter && teamFilterOptions.some(o => o.value === teamFilter))
        ? teamFilter
        : null;

    const volunteerGroupedTeams = useMemo(() => {
        const groups = [];
        event?.TaskInstances?.filter(t => t?.Task?.dueType === "EVENT_VOLUNTEER").forEach(t => {
            const team = t?.VolunteerTeam || t?.Task?.VolunteerTeamTasks?.[0]?.VolunteerTeam;
            const teamName = team?.name || "Uncategorized";
            const teamID = team?.id || null;
            const assigners = team?.VolunteerTeamAssigners || [];
            const foundIndex = groups.findIndex(g => g.team === teamName);
            if (foundIndex === -1) {
                groups.push({ team: teamName, teamID, assigners, tasks: [t] });
            } else {
                groups[foundIndex].tasks.push(t);
            }
        });
        // Sort tasks within each team by VolunteerTeamTasks.position, then title
        groups.forEach(g => {
            g.tasks.sort((a, b) => {
                const aVtt = a.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === a.volunteerTeamID);
                const bVtt = b.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === b.volunteerTeamID);
                return (aVtt?.position ?? 0) - (bVtt?.position ?? 0) || (a.Task?.title || '').localeCompare(b.Task?.title || '');
            });
        });
        const sorted = groups.sort((a, b) => a.team.localeCompare(b.team));
        if (effectiveTeamFilter) {
            return sorted.filter(g => g.teamID === effectiveTeamFilter);
        }
        return sorted;
    }, [event?.TaskInstances, effectiveTeamFilter]);

    // All tasks for the team currently open in the Manage Tasks modal, paired
    // with any existing TaskInstance on this event instance (so the modal can
    // show Add vs Remove per task). Includes tasks with NO instance attached.
    const manageTeamTasks = useMemo(() => {
        if (!manageTasksTeam) return [];
        const teamID = manageTasksTeam.teamID;
        const allTeamTasks = (availableVolunteerTasks.data || []).filter(t =>
            t?.VolunteerTeamTasks?.some(v => v.volunteerTeamID === teamID)
        );
        const existingInstances = (event?.TaskInstances || []).filter(ti =>
            ti?.Task?.dueType === "EVENT_VOLUNTEER" &&
            ti.volunteerTeamID === teamID
        );
        const instanceByTaskID = new Map();
        existingInstances.forEach(ti => instanceByTaskID.set(ti.taskID, ti));
        return allTeamTasks
            .map(t => ({ task: t, instance: instanceByTaskID.get(t.id) || null }))
            .sort((a, b) => {
                const aVtt = a.task.VolunteerTeamTasks?.find(v => v.volunteerTeamID === teamID);
                const bVtt = b.task.VolunteerTeamTasks?.find(v => v.volunteerTeamID === teamID);
                return (aVtt?.position ?? 0) - (bVtt?.position ?? 0) || (a.task.title || '').localeCompare(b.task.title || '');
            });
    }, [manageTasksTeam, availableVolunteerTasks.data, event?.TaskInstances]);

    const addTaskInstanceForTeam = useCallback((task) => {
        if (!manageTasksTeam || !event?.id) return;
        createTaskInstanceMutation.mutate({
            eventInstanceID: event.id,
            taskIDs: [{ id: task.id, volunteerTeamID: manageTasksTeam.teamID }],
            dueDate: event?.startTime,
        });
    }, [manageTasksTeam, event?.id, event?.startTime, createTaskInstanceMutation]);

    // Team options for the Add Task modal — all org volunteer teams
    const addTaskTeamOptions = useMemo(() =>
        volunteerTeams
            .map(t => ({ label: t.name, value: t.id }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        [volunteerTeams]
    );

    // Tasks for the team selected in the Add Task modal, paired with whether
    // a TaskInstance already exists on this event instance (so the modal can
    // gray out / disable already-added tasks).
    const addTaskSelectableTasks = useMemo(() => {
        if (!addTaskSelectedTeamID) return [];
        const team = volunteerTeams.find(t => t.id === addTaskSelectedTeamID);
        if (!team?.Tasks) return [];
        const existingTaskIDs = new Set(
            (event?.TaskInstances || [])
                .filter(ti => ti?.Task?.dueType === "EVENT_VOLUNTEER" && ti.volunteerTeamID === addTaskSelectedTeamID)
                .map(ti => ti.taskID)
        );
        return team.Tasks.map(t => ({ task: t, isExisting: existingTaskIDs.has(t.id) }));
    }, [addTaskSelectedTeamID, volunteerTeams, event?.TaskInstances]);

    const handleConfirmAddTasks = useCallback(() => {
        if (!addTaskSelectedTeamID) {
            Toast.show({ type: 'error', text2: 'Select a team first.' });
            return;
        }
        if (addTaskSelectedTaskIDs.length === 0) {
            Toast.show({ type: 'error', text2: 'Select at least one task to add.' });
            return;
        }
        if (!event?.id) return;
        createTaskInstanceMutation.mutate({
            eventInstanceID: event.id,
            taskIDs: addTaskSelectedTaskIDs.map(id => ({ id, volunteerTeamID: addTaskSelectedTeamID })),
            dueDate: event?.startTime,
        }, {
            onSuccess: () => {
                const count = addTaskSelectedTaskIDs.length;
                Toast.show({ type: 'success', text2: `${count} task${count === 1 ? '' : 's'} added.` });
                setAddTaskModalOpen(false);
                setAddTaskSelectedTeamID(null);
                setAddTaskSelectedTaskIDs([]);
            },
        });
    }, [addTaskSelectedTeamID, addTaskSelectedTaskIDs, event?.id, event?.startTime, createTaskInstanceMutation]);

    // ============ EFFECTS ============

    // Marked dates for the instance navigator calendar
    const instanceMarkedDates = useMemo(() => {
        const map = {};
        siblingInstances.forEach(inst => {
            if (!inst.startTime) return;
            const dateStr = safeNewDate(inst.startTime)?.toDateString();
            if (dateStr) {
                if (!map[dateStr]) map[dateStr] = [];
                map[dateStr].push(event?.Event?.Calendar?.color || '#3b82f6');
            }
        });
        return map;
    }, [siblingInstances, event?.Event?.Calendar?.color]);

    const currentInstanceIndex = useMemo(() => {
        return siblingInstances.findIndex(inst => inst.id === eventInstanceID);
    }, [siblingInstances, eventInstanceID]);

    const prevInstance = currentInstanceIndex > 0 ? siblingInstances[currentInstanceIndex - 1] : null;
    const nextInstance = currentInstanceIndex >= 0 && currentInstanceIndex < siblingInstances.length - 1 ? siblingInstances[currentInstanceIndex + 1] : null;

    const instanceNavigatorContent = useCallback(({ closeSheet }) => {
        const currentDate = safeNewDate(event?.startTime);
        const navigateTo = (id) => {
            closeSheet?.();
            const activeTab = Array.isArray(tab) ? tab[0] : tab;
            if (activeTab) {
                router.replace({ pathname: `/events/instances/${id}`, params: { tab: activeTab } });
            } else {
                router.replace(`/events/instances/${id}`);
            }
        };
        return (
            <View style={styles.instanceNavigator}>
                <Text style={styles.instanceNavigatorTitle}>Select an event date</Text>
                <View style={styles.navigatorButtons}>
                    <SolidButton
                        width={180}
                        text="Previous Event"
                        iconName="chevron-back"
                        iconBefore
                        onPress={() => prevInstance && navigateTo(prevInstance.id)}
                        disabled={!prevInstance}
                    />
                    <SolidButton
                        width={180}
                        text="Next Event"
                        iconName="chevron-forward"
                        iconAfter
                        onPress={() => nextInstance && navigateTo(nextInstance.id)}
                        disabled={!nextInstance}
                    />
                </View>
                <View style={styles.calendarWrapper}>
                    <Calendar
                        initialSelectedDate={currentDate}
                        markedDates={instanceMarkedDates}
                        onDayPress={(date) => {
                            const dateStr = date?.toDateString();
                            const target = siblingInstances.find(inst => safeNewDate(inst.startTime)?.toDateString() === dateStr);
                            if (target && target.id !== eventInstanceID) {
                                navigateTo(target.id);
                            }
                        }}
                        disableSelection
                        dayBoxHeightOverride={44}
                        canCreateEvents={false}
                        smallCalendar
                    />
                </View>
            </View>
        );
    }, [event?.startTime, instanceMarkedDates, prevInstance, nextInstance, siblingInstances, eventInstanceID, tab]);

    useEffect(() => {
        navigation.setOptions({
            title: event?.nameOverride || event?.Event?.name,
            headerTitle: () => <PageTitle
                title={event?.nameOverride || event?.Event?.name}
                subtitle={isMultiDay
                    ? formattedDate
                    : !event ? "Loading Event..." : (safeNewDate(event?.startTime) || safeNewDate(event?.Event?.defaultStartTime))?.toLocaleString('default', { month: "short", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) ?? ''}
                additionalContent={siblingInstances.length > 1 ? instanceNavigatorContent : undefined}
            />,
            headerRight: () => {
                const options = [];

                loggedInUser?.can?.("Edit Events") && options.push(
                    { name: "Edit This Occurrence", subtitle: "This specific Event", onSelect: () => router.push({ pathname: `/events/instances/create`, params: { id: eventInstanceID } }) },
                    { name: "Edit Event Series", subtitle: "All recurring Events", onSelect: () => router.push({ pathname: `/events/create`, params: { id: event?.Event?.id } }) },
                    { name: (event?.canceled ? "Reschedule Event" : "Cancel Event"), onSelect: () => cancelEventMutation.mutate({ eventInstanceID: eventInstanceID, canceled: !event?.canceled }) }
                );

                const hasServiceFlowItems = serviceFlowItems?.length > 0;
                hasServiceFlowItems && organization && !isServiceFlowV2Available(organization) && options.push({
                    name: "Export Service Flow",
                    subtitle: "Download as PDF",
                    onSelect: async () => {
                        try {
                            await ServiceFlowExporter.exportToPDF({ ...event, ServiceFlowInstances: serviceFlowItems }, { timezone: organization?.timezone || null });
                        } catch (error) {
                            Sentry.captureException(error);
                            Toast.show({ type: 'error', text2: 'Failed to export Service Flow' });
                        }
                    }
                });

                loggedInUser?.can?.("Delete Events") && options.push({ name: "Delete this Occurrence", destructive: true, onSelect: () => deleteEventMutation.mutate({ eventInstanceID: eventInstanceID, isDeleted: !event?.isDeleted }) });

                return <DropdownMenu testID="HeaderMenu" options={options} />;
            }
        });
    }, [navigation, event, loggedInUser, serviceFlowItems, siblingInstances, instanceNavigatorContent, organization?.timezone]);

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) });

    // ============ TAB ROUTES ============
    // Stabilize routes based on permissions (not data length) to prevent tab jumping
    const canEditEvents = loggedInUser?.can?.("Edit Events");
    const canCreateEvents = loggedInUser?.can?.("Create Events");
    const canCheckIn = loggedInUser?.can?.("Check In");
    const canRegisterPeople = loggedInUser?.can?.("Register People");
    const canCreatePaths = loggedInUser?.can?.("Create Paths");
    const tabRoutes = useMemo(() => {
        const routes = [{ key: 'info', title: 'Info' }];
        // Always show these tabs if user can edit, to prevent route changes when data loads
        if (canEditEvents || serviceFlowItems.length > 0) routes.push({ key: 'serviceFlow', title: 'Service Flow' });
        if (canEditEvents || volunteerGroupedTeams.length > 0) routes.push({ key: 'volunteers', title: 'Volunteers' });
        if (event?.Songs?.length > 0) routes.push({ key: 'music', title: 'Music' });
        if (showRegistration) routes.push({ key: 'registrants', title: 'Registration' });
        if (isNative && isAndroidMapsEnabled && locationWithAddress) routes.push({ key: 'map', title: 'Map' });
        return routes;
    }, [canEditEvents, canCheckIn, serviceFlowItems.length, volunteerGroupedTeams.length, event?.Songs?.length, showRegistration, isNative, isAndroidMapsEnabled, locationWithAddress]);

    // Apply tab query param on mount / navigation
    useEffect(() => {
        if (!tab) return;
        const targetTab = Array.isArray(tab) ? tab[0] : tab;
        const normalIndex = tabRoutes.findIndex(r => r.key === targetTab);
        if (normalIndex !== -1) setTabIndex(normalIndex);
        const wideRoutes = tabRoutes.filter(r => r.key !== 'info');
        const wideIndex = wideRoutes.findIndex(r => r.key === targetTab);
        if (wideIndex !== -1) setWideTabIndex(wideIndex);
    }, [tab, tabRoutes]);

    // ============ INFO ROW COMPONENT ============
    const InfoRow = useCallback(({ icon, label, value, onPress, children }) => {
        if (!value && !children) return null;
        const content = (
            <View style={styles.infoRow}>
                <View style={styles.infoRowLeft}>
                    <Unicon name={icon} size={18} colorVariant="textSecondary" />
                    <Text style={styles.infoRowLabel}>{label}</Text>
                </View>
                <View style={styles.infoRowRight}>
                    {children || <Text style={[styles.infoRowValue, onPress && styles.linkText]}>{value}</Text>}
                </View>
            </View>
        );
        return onPress ? <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity> : content;
    }, []);

    // ============ ADD TASK BUTTON ============
    // ============ EVENT INFO PANEL ============
    const EventInfoPanel = useCallback(() => (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={fetchingEvents} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
            >
                {/* Hero Image */}
                {event?.archiveVideoThumbnailUrl && (
                    <View style={styles.heroContainer}>
                        <Image source={{ uri: event.archiveVideoThumbnailUrl }} style={styles.heroImage} contentFit="cover" />
                        <LinearGradient colors={['transparent', styles.gradientEnd.backgroundColor]} style={styles.heroGradient} />
                    </View>
                )}

                {/* Header Card */}
                <View style={styles.headerCard}>
                    {event?.canceled && (
                        <View style={styles.canceledBadge}>
                            <Unicon name="close-circle" size={14} colorVariant="error" />
                            <Text style={styles.canceledText}>Event Canceled</Text>
                        </View>
                    )}

                    {extensionSource?.MainInstance && (
                        <View style={styles.extensionNote}>
                            <Unicon name="link-outline" size={14} colorVariant="primary" />
                            <Text style={styles.extensionNoteText}>
                                Registration extended from {extensionSource.MainInstance.nameOverride || extensionSource.MainInstance.Event?.name}
                                {extensionSource.MainInstance.startTime ? ` · ${(safeNewDate(extensionSource.MainInstance.startTime))?.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", timeZone: organization?.timezone }) || ''}` : ''}
                            </Text>
                        </View>
                    )}

                    <View style={styles.eventTitleRow}>
                        <View style={styles.calendarBadge}>
                            <Text style={styles.calendarBadgeMonth}>
                                {(safeNewDate(event?.startTime) || safeNewDate(event?.Event?.defaultStartTime))?.toLocaleString('default', { month: 'short' }).toUpperCase() ?? ''}
                            </Text>
                            <Text style={styles.calendarBadgeDay}>
                                {(safeNewDate(event?.startTime) || safeNewDate(event?.Event?.defaultStartTime))?.getDate() ?? ''}
                            </Text>
                        </View>
                        <View style={styles.eventTitleInfo}>
                            <Pressable onPress={() => event?.Event?.id && router.push({pathname: "/events/" + event.Event.id})} style={{flexDirection:'row', gap:2, alignItems:"center"}}>
                                <Text style={[styles.eventTitle, event?.canceled && styles.strikethrough]}>{event?.nameOverride || event?.Event?.name}</Text>
                                <Unicon name="chevron-forward" size={18} colorVariant="text"/>
                            </Pressable>
                            <View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                    <View style={styles.calendarDot(event?.Event?.Calendar?.color)}></View>
                                    <Text style={styles.eventSubtitle}>{event?.Event?.Calendar?.name}</Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    {/* Quick Stats */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Unicon name="time" size={16} colorVariant="textSecondary" />
                            <View>
                                <Text style={styles.statLabel}>Time</Text>
                                <Text style={[styles.statValue, event?.canceled && styles.strikethrough]}>{formattedTime || 'All Day'}</Text>
                            </View>
                        </View>
                        {event?.Locations?.length > 0 && (
                            <>
                                <View style={styles.statDivider} />
                                <TouchableOpacity
                                    style={styles.statItem}
                                    onPress={() => event.Locations.length === 1 && event.Locations[0]?.id && router.push('/locations/' + event.Locations[0].id)}
                                    disabled={event.Locations.length !== 1}
                                >
                                    <Unicon name="location" size={16} colorVariant="textSecondary" />
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.statLabel}>{event.Locations.length > 1 ? 'Locations' : 'Location'}</Text>
                                        <Text style={[styles.statValue, event.Locations.length === 1 && styles.linkText]} numberOfLines={1}>
                                            {event.Locations.length > 1
                                                ? `${event.Locations.length} Locations`
                                                : (event.Locations[0]?.name || event.Locations[0]?.detailedArea)}
                                        </Text>
                                    </View>
                                </TouchableOpacity>
                            </>
                        )}
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {loggedInUser?.can?.("Check In") && (
                            <ActionButton 
                                iconName="qr-code" 
                                text="Check In" 
                                onPress={() => router.push(`/events/instances/${eventInstanceID}/checkin`)} 
                            />
                        )}


                        <DropdownMenu
                            triggerComponent={(
                                <ActionButton 
                                    iconName="calendar-plus" 
                                    iconLibrary="material"
                                    text="Add to Calendar"
                                />
                            )}
                            options={[
                                { name: `Subscribe in ${isIos ? "Apple" : "Google"} Calendar`, onSelect: () => Linking.openURL(`${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${event?.Event?.Calendar?.id}.ics`) },
                                { name: "Share Calendar Link", onSelect: async () => {
                                    await Share.share({
                                        message: isIos ? "Subscribe to the Reach calendar!" : `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${event?.Event?.Calendar?.id}.ics`,
                                        url: `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${event?.Event?.Calendar?.id}.ics`
                                    });
                                }},
                            ]}
                        />
                        
                        {locationWithAddress && (
                            <ActionButton
                                iconName="navigate"
                                text="Directions"
                                onPress={() => {
                                    try{
                                        locationWithAddress 
                                            ? Linking.canOpenURL(google_maps_url).then((canOpen) => Linking.openURL(canOpen ? google_maps_url : apple_maps_url)) 
                                            : null
                                    }catch(e){
                                        Sentry.logger.warn("Couldn't open maps app: " + e.message);
                                        Toast.show({
                                            type: "error",
                                            text1: "Error",
                                            text2: "Unable to open maps application for directions."
                                        })
                                    }
                                }}
                            />
                        )}
                    </View>
                </View>

                {/* Info Section */}
                <View style={styles.infoSection}>
                    <InfoRow icon="calendar" label="Date">
                        <Text style={[styles.infoRowValue, event?.canceled && styles.strikethrough]}>{formattedDate}</Text>
                    </InfoRow>

                    {!isMultiDay && formattedTime && (
                        <InfoRow icon="time" label="Time">
                            <Text style={[styles.infoRowValue, event?.canceled && styles.strikethrough]}>{formattedTime}</Text>
                        </InfoRow>
                    )}

                    <InfoRow icon="albums" label="Calendar" value={<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <View style={[styles.calendarDot(event?.Event?.Calendar?.color), {marginTop:0}]}></View>
                        {loggedInUser?.can?.("Edit Calendars") ? (
                            <TouchableOpacity onPress={() => router.push({ pathname: "/events/calendars/create", params: { id: event?.Event?.Calendar?.id } })}>
                                <Text style={[styles.infoRowValue, styles.linkText, styles.underlineText]}>{event?.Event?.Calendar?.name}</Text>
                            </TouchableOpacity>
                        ) : (
                            <Text style={styles.infoRowValue}>{event?.Event?.Calendar?.name}</Text>
                        )}
                    </View>} />

                    {event?.Locations?.length > 0 && (
                        <InfoRow
                            icon="location"
                            label="Location(s)"
                        >
                            <View>
                                {sortedLocations.map((loc, idx) => (
                                    <TouchableOpacity
                                        key={idx}
                                        style={{ marginBottom: idx < sortedLocations.length - 1 ? 8 : 0 }}
                                        onPress={() => loc?.id && router.push('/locations/' + loc.id)}
                                        disabled={!loc?.id}
                                    >
                                        <Text style={[styles.infoRowValue, loc?.id && styles.linkText]}>
                                            {loc.name || loc.detailedArea}
                                        </Text>
                                        {loc.detailedArea && loc.name ? <Text style={styles.infoRowValueSub}>{loc.detailedArea}</Text> : null}
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </InfoRow>
                    )}

                    {eventSpeakers.length > 0 && (
                        <InfoRow icon="mic" label={eventSpeakers.length > 1 ? "Speakers" : "Speaker"}>
                            <View>
                                {eventSpeakers.map((speaker, idx) => (
                                    <Text key={speaker.id || idx} style={styles.infoRowValue}>
                                        {speaker.firstName} {speaker.lastName}
                                    </Text>
                                ))}
                            </View>
                        </InfoRow>
                    )}
                </View>

                {/* Notes Section */}
                {eventNotes && (
                    <View style={styles.notesSection}>
                        <Text style={styles.sectionLabel}>Notes</Text>
                        <View style={styles.notesCard}>
                            <Hyperlink
                                onPress={(url) => isWeb ? window.open(url, "_blank") : router.push({ pathname: "(utilities)/webview", params: { url } })}
                                linkStyle={styles.linkText}
                            >
                                <Text style={styles.notesText}>{eventNotes}</Text>
                            </Hyperlink>
                        </View>
                    </View>
                )}

                {/* Links Section */}
                {event?.Links?.length > 0 && (
                    <View style={styles.linksSection}>
                        <Text style={styles.sectionLabel}>Links</Text>
                        <View style={styles.linksGrid}>
                            {event.Links.map((link, idx) => (
                                <View key={link.url || idx} style={styles.linkItem}>
                                    <LinkPreview url={link.url} linkObject={link} widthDivisor={width > 600 ? 280 : width - 72} containerWidth={width > 600 ? 280 : width - 72} />
                                </View>
                            ))}
                        </View>
                    </View>
                )}

                {/* Video Section */}
                {event?.archiveVideoID && (
                    <View style={styles.videoSection}>
                        <Text style={styles.sectionLabel}>Watch</Text>
                        <Pressable
                            style={styles.videoCard}
                            onPress={() => isWeb ? window.open(`https://youtube.com/watch?v=${event.archiveVideoID}`, "_blank") : router.push({ pathname: "(utilities)/webview", params: { url: `https://youtube.com/watch?v=${event.archiveVideoID}` } })}
                        >
                            <Image source={{ uri: event.archiveVideoThumbnailUrl }} style={styles.videoThumbnail} contentFit="cover" />
                            <View style={styles.playOverlay}>
                                <View style={styles.playButton}>
                                    <Unicon name="play" size={32} colorVariant="text" />
                                </View>
                            </View>
                        </Pressable>
                    </View>
                )}
            </ScrollView>
        </View>
    ), [event, fetchingEvents, loggedInUser, eventSpeakers, eventNotes, formattedDate, formattedTime, isMultiDay, locationWithAddress, isNative, isWeb, width, extensionSource, organization?.timezone]);

    // ============ TAB SCENES ============
    const renderTabBar = useCallback((props) => <ReachTabBar {...props}  scrollEnabled={tabRoutes.length > 3} />, [tabRoutes.length]);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'info':
                return <EventInfoPanel />;

            case 'serviceFlow':
                return (
                    <View style={styles.serviceFlowTabContent}>
                        <ServiceFlowInstancePanel
                            eventInstanceID={eventInstanceID}
                            event={event}
                            canEdit={!!loggedInUser?.can?.("Edit Events")}
                        />
                    </View>
                );

            case 'volunteers':
                return (
                    <View style={styles.tabContent}>
                        <LegendList
                            keyboardShouldPersistTaps="handled"
                            keyExtractor={(item) => item.team}
                            estimatedItemSize={200}
                            contentContainerStyle={styles.taskListPadding}
                            data={volunteerGroupedTeams.map(cat => ({
                                ...cat,
                                tasks: cat.tasks
                            })).filter(cat => cat.tasks.length > 0)}
                            refreshControl={<RefreshControl refreshing={fetchingEvents} onRefresh={refresh} />}
                            ListEmptyComponent={<EmptyListPlaceholder message="No volunteer tasks" />}
                            ListHeaderComponent={
                                <View style={styles.taskHeaderRow}>
                                    {teamFilterOptions.length > 1 && (
                                        <View style={styles.locationFilterContainer}>
                                            <SelectInput
                                                title="Filter by Team"
                                                placeholder="All Teams"
                                                value={effectiveTeamFilter}
                                                options={teamFilterOptions}
                                                onUpdate={(value) => useTeamFilterStore.getState().setTeamID(value ?? null)}
                                            />
                                        </View>
                                    )}
                                    <View style={styles.taskHeaderButtons}>
                                        <IconButton
                                            iconName="calendar-outline"
                                            iconSize={20}
                                            type="primary"
                                            tooltipText="Schedule"
                                            testID="VolunteersScheduleButton"
                                            onPress={() => router.push(`/tasks/schedule?eventID=${event?.Event?.id}`)}
                                        />
                                        {loggedInUser?.can?.("Update Tasks") && (
                                            <IconButton
                                                iconName="add-outline"
                                                iconSize={20}
                                                type="primary"
                                                tooltipText="Add Task"
                                                testID="VolunteersAddTaskButton"
                                                onPress={() => {
                                                    setAddTaskSelectedTeamID(null);
                                                    setAddTaskSelectedTaskIDs([]);
                                                    setAddTaskModalOpen(true);
                                                }}
                                            />
                                        )}
                                        {/* Volunteer tasks are managed via the Manage Tasks modal per team */}
                                    </View>
                                </View>
                            }
                            renderItem={({ item }) => {
                                const teamTemplates = taskTemplates?.filter(t => t.volunteerTeamID === item.teamID) || [];
                                return (
                                <View style={styles.taskItemWrapper}>
                                    <View style={styles.categorySection}>
                                        <View style={styles.teamHeaderRow}>
                                            <Text style={styles.categoryTitle}>{item.team}</Text>
                                            {loggedInUser?.can?.("Update Tasks") && (loggedInUser?.isAdmin || item.assigners?.some(a => a.personID === loggedInUser?.id)) && (
                                                <View style={styles.teamHeaderActions}>
                                                    <SolidButton
                                                        size="sm"
                                                        type="primary"
                                                        variant="muted"
                                                        iconBefore
                                                        iconName="list-outline"
                                                        text="Manage"
                                                        testID={`ManageTasks-${item.teamID || item.team}`}
                                                        onPress={() => setManageTasksTeam({ teamID: item.teamID, team: item.team, assigners: item.assigners })}
                                                    />
                                                    {teamTemplates.length > 0 && (
                                                        <SolidButton
                                                            size="sm"
                                                            type="primary"
                                                            variant="muted"
                                                            iconBefore
                                                            iconName="clipboard-text-multiple"
                                                            iconLibrary="material"
                                                            text={isNative ? "Assign" : "Apply Template"}
                                                            onPress={() => {
                                                                setSelectedTemplateTeamID(item.teamID);
                                                                setTemplateModalOpen(true);
                                                            }}
                                                        />
                                                    )}
                                                    {!!item.teamID && (
                                                        <SolidButton
                                                            size="sm"
                                                            type="primary"
                                                            variant="muted"
                                                            iconBefore
                                                            iconName="add-outline"
                                                            text={isNative ? "Create" : "Create Template"}
                                                            onPress={() => {
                                                                const prefillMembers = [];
                                                                let position = 0;
                                                                item.tasks.forEach(t => {
                                                                    (t.Assignees || []).forEach(a => {
                                                                        if (a.Person?.id) {
                                                                            prefillMembers.push({
                                                                                taskID: t.taskID,
                                                                                position: position++,
                                                                                Person: a.Person,
                                                                            });
                                                                        }
                                                                    });
                                                                });
                                                                router.push({
                                                                    pathname: '/tasks/templates/create',
                                                                    params: {
                                                                        teamID: item.teamID,
                                                                        defaultName: `${item.team} Volunteers`,
                                                                        prefill: JSON.stringify(prefillMembers),
                                                                    }
                                                                });
                                                            }} 
                                                        />
                                                    )}
                                                </View>
                                            )}
                                        </View>
                                        {item.tasks.map(t => (
                                            <TaskCard
                                                key={t.id}
                                                taskInstance={{ ...t, EventInstance: { ...event } }}
                                                showEvent={false}
                                                fromScreen={`events/instances/${eventInstanceID}`}
                                                actions={[]}
                                            />
                                        ))}
                                    </View>
                                </View>
                            );
                        }}
/>
                    </View>
                );

            case 'music':
                return (
                    <View style={styles.tabContent}>
                        {event?.Songs?.length > 0 ? (
                            <LegendList
                                recycleItems
                                keyExtractor={(item) => item.id}
                                estimatedItemSize={100}
                                contentContainerStyle={styles.listPadding}
                                data={event.Songs}
                                refreshControl={<RefreshControl refreshing={fetchingEvents} onRefresh={refresh} />}
                                renderItem={({ item }) => <SongCard key={item.id} song={item} />}
                                keyboardShouldPersistTaps="handled"
                            />
                        ) : (
                            <EmptyListPlaceholder message="No songs scheduled for this event" />
                        )}
                    </View>
                );

            case 'registrants':
                return (
                    <View style={styles.tabContent}>
                        <LegendList
                            keyExtractor={(item) => item._type === 'groupHeader' ? item.key : item.personID}
                            estimatedItemSize={72}
                            contentContainerStyle={styles.listPadding}
                            data={filteredAndSortedRegistrants}
                            extraData={[canEditEvents, canCheckIn, registrantSearch, registrantSort]}
                            refreshControl={<RefreshControl refreshing={fetchingRegistrants} onRefresh={refetchRegistrants} />}
                            ListHeaderComponent={
                                <>
                                    {pendingRegistrants.length > 0 && (
                                        <View style={styles.pendingSection}>
                                            <Text style={styles.pendingSectionTitle}>
                                                {pendingRegistrants.length} Pending {pendingRegistrants.length === 1 ? 'Registration' : 'Registrations'}
                                            </Text>
                                            {pendingRegistrants.map((item) => {
                                                const isProcessing = resolvePendingMutation.isPending && resolvePendingMutation.variables?.pendingID === item.id;
                                                const isDeleting = deletePendingMutation.isPending && deletePendingMutation.variables?.pendingID === item.id;
                                                const linkedPerson = pendingLinkPerson[item.id];
                                                return (
                                                    <View key={item.id} style={styles.pendingCard}>
                                                        <View style={styles.pendingCardHeader}>
                                                            <View style={styles.pendingAvatarPlaceholder}>
                                                                <Text style={styles.pendingAvatarInitials}>
                                                                    {item.firstName?.[0]}{item.lastName?.[0]}
                                                                </Text>
                                                            </View>
                                                            <View style={styles.pendingInfo}>
                                                                <Text style={styles.pendingName}>{item.firstName} {item.lastName}</Text>
                                                                {item.email ? <Text style={styles.pendingMeta}>{item.email}</Text> : null}
                                                                {item.phone ? <Text style={styles.pendingMeta}>{item.phone}</Text> : null}
                                                            </View>
                                                            <View style={styles.pendingBadge}>
                                                                <Unicon name="time-outline" size={12} colorVariant="warning" />
                                                                <Text style={styles.pendingBadgeText}>Pending</Text>
                                                            </View>
                                                            <Pressable
                                                                onPress={() => showAlert({
                                                                    title: 'Remove Pending Registration',
                                                                    subtitle: `Remove ${item.firstName} ${item.lastName} from pending registrations?`,
                                                                    confirmLabel: 'Remove',
                                                                    cancelLabel: 'Cancel',
                                                                    onConfirm: () => deletePendingMutation.mutate({ pendingID: item.id }),
                                                                    onCancel: () => {},
                                                                })}
                                                                style={styles.pendingDeleteButton}
                                                                disabled={isDeleting}
                                                            >
                                                                <Unicon name="close" size={16} style={styles.pendingDeleteIcon} />
                                                            </Pressable>
                                                        </View>
                                                        <View style={styles.pendingActions}>
                                                            {loggedInUser?.can?.("Register People") &&
                                                                <View style={styles.pendingLinkRow}>
                                                                    <View style={{ flex: 1 }}>
                                                                        <SheetSelector
                                                                            type="people"
                                                                            title="Link to Person"
                                                                            placeholder="Search people..."
                                                                            value={linkedPerson}
                                                                            onChange={(selected) => setPendingLinkPerson(prev => ({ ...prev, [item.id]: selected }))}
                                                                        />
                                                                    </View>
                                                                    <View style={{marginBottom:13}}>
                                                                        <SolidButton
                                                                            size="sm"
                                                                            type="primary"
                                                                            text="Link"
                                                                            iconName="link-outline"
                                                                            iconBefore
                                                                            disabled={!linkedPerson?.id || isProcessing}
                                                                            processing={isProcessing && !!linkedPerson?.id}
                                                                            onPress={() => resolvePendingMutation.mutate({ pendingID: item.id, eventInstanceID: registrationInstanceID, personID: linkedPerson.id })}
                                                                        />
                                                                    </View>
                                                                </View>
                                                            }

                                                            {loggedInUser?.can?.("Create People") &&
                                                                <SolidButton
                                                                    size="sm"
                                                                    type="success"
                                                                    text="Create New Person"
                                                                    iconName="person-add-outline"
                                                                    iconBefore
                                                                    processing={isProcessing && !linkedPerson?.id}
                                                                    disabled={isProcessing}
                                                                    onPress={() => showAlert({
                                                                        title: `Create new person for ${item.firstName}?`,
                                                                        subtitle: 'A new person record will be created and registered for this event.',
                                                                        confirmLabel: 'Create & Register',
                                                                        cancelLabel: 'Cancel',
                                                                        onConfirm: () => resolvePendingMutation.mutate({ pendingID: item.id, eventInstanceID: registrationInstanceID, createNew: true }),
                                                                        onCancel: () => {},
                                                                    })}
                                                                />
                                                            }
                                                        </View>
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    )}
                                    <View style={styles.registrantsHeader}>
                                        <View style={styles.registrantsTopRow}>
                                            <View style={styles.registrantsButtonsRow}>
                                                {(canRegisterPeople || canCheckIn) && (
                                                    <View style={styles.registrantsButtonFlex}>
                                                        <SheetSelector
                                                            type="people"
                                                            title="Register a Person"
                                                            fromScreen={`events/instances/${eventInstanceID}`}
                                                            addScreenName="people/create"
                                                            multiple
                                                            forceModal
                                                            value={registerPickerValue}
                                                            onChange={(selected) => {
                                                                const people = (Array.isArray(selected) ? selected : [selected]).filter(p => p?.id);
                                                                setRegisterPickerValue(people);
                                                                if (people.length === 0) return;
                                                                // On native, presenting our Modal while the picker's
                                                                // SheetModal is mid-dismiss can drop it — defer.
                                                                if (isNative) setTimeout(() => setPendingRegistrationPeople(people), 400);
                                                                else setPendingRegistrationPeople(people);
                                                            }}
                                                        >
                                                            <SolidButton size="sm" type="success" variant="muted" iconName="add" text="Register" width="100%" testID="register-people-button" />
                                                        </SheetSelector>
                                                    </View>
                                                )}
                                                {canCreatePaths && (
                                                    <View style={styles.registrantsButtonFlex}>
                                                        <SolidButton
                                                            size="sm"
                                                            iconName="git-branch-outline"
                                                            variant="muted"
                                                            text="Automations"
                                                            onPress={() => setPathsSheetOpen(true)}
                                                            testID="registrant-paths-manage-button"
                                                            width="100%"
                                                        />
                                                    </View>
                                                )}
                                                {isNative && (
                                                    <View style={styles.registrantsButtonFlex}>
                                                        <SolidButton
                                                            size="sm"
                                                            variant="muted"
                                                            iconName="qr-code"
                                                            text="Scan Code"
                                                            onPress={() => router.push({
                                                                pathname: `/events/instances/${registrationInstanceID}/scan-registration-qr`,
                                                                params: { eventInstanceID: registrationInstanceID, defaultLocationID: event?.EventInstanceLocations?.[0]?.locationID },
                                                            })}
                                                            testID="registrant-scan-qr-button"
                                                            width="100%"
                                                        />
                                                    </View>
                                                )}
                                            </View>
                                            <View style={styles.registrantsSearchRow}>
                                                <View style={styles.registrantsSearchFull}>
                                                    <SearchBox
                                                        searchText={registrantSearch}
                                                        placeholder="Search registrants..."
                                                        onChangeText={setRegistrantSearch}
                                                        textChangeDelay={200}
                                                    />
                                                </View>
                                                <DropdownMenu
                                                    triggerComponent={
                                                        <Pressable style={styles.registrantSortButton}>
                                                            <Unicon name="sort" library="material" size={16} colorVariant="textSecondary" />
                                                        </Pressable>
                                                    }
                                                    options={[
                                                        { name: 'Name (A-Z)', onSelect: () => setRegistrantSort('name-asc') },
                                                        { name: 'Name (Z-A)', onSelect: () => setRegistrantSort('name-desc') },
                                                        { name: 'Registered (Oldest)', onSelect: () => setRegistrantSort('registeredAt-asc') },
                                                        { name: 'Registered (Newest)', onSelect: () => setRegistrantSort('registeredAt-desc') },
                                                    ]}
                                                />
                                            </View>
                                        </View>
                                        <View style={styles.registrantsCountRow}>
                                            {canEditEvents && !isRegistrationExtension && (
                                                <Pressable onPress={() => setExtendModalOpen(true)} testID="extend-registration-button">
                                                    <Text style={styles.extendRegistrationText}>Extend Registration</Text>
                                                </Pressable>
                                            )}
                                            {isRegistrationExtension && extensionSource?.MainInstance && (
                                                <Text style={styles.registrantsManagedByText}>
                                                    Registration managed by {extensionSource.MainInstance.nameOverride || extensionSource.MainInstance.Event?.name}
                                                    {extensionSource.MainInstance.startTime ? ` · ${(safeNewDate(extensionSource.MainInstance.startTime))?.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", timeZone: organization?.timezone }) || ''}` : ''}
                                                </Text>
                                            )}
                                            <Text style={styles.registrantsCountSubtext}>
                                                {filteredAndSortedRegistrants.filter(r => r._type !== 'groupHeader').length} {filteredAndSortedRegistrants.filter(r => r._type !== 'groupHeader').length === 1 ? 'registrant' : 'registrants'}  ·  {filteredAndSortedRegistrants.filter(r => r._type !== 'groupHeader' && r.isCheckedIn).length} checked in
                                            </Text>
                                        </View>
                                    </View>
                                </>
                            }
                            ListEmptyComponent={<EmptyListPlaceholder message="No one has registered for this event yet" />}
                            renderItem={({ item }) => {
                                if (item._type === 'groupHeader') {
                                    return (
                                        <View style={styles.registrantDateGroupHeader}>
                                            <Text style={styles.registrantDateGroupLabel}>{item.label}</Text>
                                        </View>
                                    );
                                }
                                const isCheckedIn = item.isCheckedIn;

                                const handleCodes = () => setCodesSheetRegistrant({
                                    personID: item.personID,
                                    personName: `${item.Person?.firstName || ''} ${item.Person?.lastName || ''}`.trim(),
                                });
                                const handleRemoveRegistrant = () => showAlert({
                                    title: 'Remove Registrant',
                                    subtitle: 'Remove this person from the registrant list?',
                                    confirmLabel: 'Remove',
                                    cancelLabel: 'Cancel',
                                    onConfirm: () => removeRegistrantMutation.mutate({ personID: item.personID, eventInstanceID: registrationInstanceID }),
                                    onCancel: () => {},
                                });
                                const handleRemoveCheckIn = () => removeCheckInMutation.mutate({ attendeeID: item.personID, eventInstanceID });

                                // Build the action list for this registrant
                                // On mobile with multiple locations, expand each location as a separate "Check In → <loc>" option
                                const locs = event?.Locations || [];
                                const checkInOptions = canCheckIn && !isCheckedIn
                                    ? locs.length <= 1
                                        ? [{
                                            name: 'Check In',
                                            icon: { android: 'checkmark', ios: 'checkmark.circle', web: 'checkmark-circle-outline' },
                                            onSelect: () => checkInRegistrantMutation.mutate({
                                                eventInstanceID,
                                                personID: item.personID,
                                                submitterID: loggedInUser?.id,
                                                locationID: locs[0]?.id,
                                            }),
                                        }]
                                        : locs.map(loc => ({
                                            name: `Check In — ${loc.name || loc.detailedArea}`,
                                            icon: { android: 'checkmark', ios: 'checkmark.circle', web: 'checkmark-circle-outline' },
                                            onSelect: () => checkInRegistrantMutation.mutate({
                                                eventInstanceID,
                                                personID: item.personID,
                                                submitterID: loggedInUser?.id,
                                                locationID: loc.id,
                                            }),
                                        }))
                                    : [];

                                const removeCheckInOption = canCheckIn && isCheckedIn ? {
                                    name: 'Remove Check In',
                                    icon: { android: 'close', ios: 'xmark.circle', web: 'close-circle-outline' },
                                    destructive: true,
                                    onSelect: handleRemoveCheckIn,
                                } : null;

                                const codesOption = canCreateEvents ? {
                                    name: 'Codes',
                                    icon: { android: 'qr_code', ios: 'qrcode', web: 'qr-code' },
                                    onSelect: handleCodes,
                                } : null;

                                const removeOption = canEditEvents ? {
                                    name: 'Remove',
                                    icon: { android: 'delete', ios: 'trash', web: 'trash' },
                                    destructive: true,
                                    onSelect: handleRemoveRegistrant,
                                } : null;

                                const actionOptions = [...checkInOptions, removeCheckInOption, codesOption, removeOption].filter(Boolean);

                                // Web: inline buttons. Mobile: dropdown menu.
                                const registrantButtons = isNative ? (
                                    actionOptions.length > 0 ? (
                                        <DropdownMenu
                                            testID={`registrant-menu-${item.personID}`}
                                            options={actionOptions}
                                        />
                                    ) : null
                                ) : (
                                    <View style={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
                                        {canCheckIn && !isCheckedIn && (
                                            <DropdownMenu
                                                key="checkin"
                                                triggerComponent={
                                                    <SolidButton
                                                        size="sm"
                                                        iconName="checkmark-circle-outline"
                                                        text="Check In"
                                                        processing={checkInRegistrantMutation.isPending && checkInRegistrantMutation.variables?.personID === item.personID}
                                                    />
                                                }
                                                options={event?.Locations?.map(loc => ({
                                                    name: loc.name || loc.detailedArea,
                                                    onSelect: () => checkInRegistrantMutation.mutate({
                                                        eventInstanceID,
                                                        personID: item.personID,
                                                        submitterID: loggedInUser?.id,
                                                        locationID: loc.id,
                                                    })
                                                })) || []}
                                            />
                                        )}
                                        {canCheckIn && isCheckedIn && (
                                            <SolidButton
                                                key="removeCheckIn"
                                                size="sm"
                                                type="error"
                                                variant="muted"
                                                iconName="close"
                                                text="Remove Check In"
                                                processing={removeCheckInMutation.isPending && removeCheckInMutation.variables?.attendeeID === item.personID}
                                                onPress={handleRemoveCheckIn}
                                                testID={`registrant-remove-checkin-${item.personID}`}
                                            />
                                        )}
                                        {canCreateEvents && (
                                            <SolidButton
                                                key="codes"
                                                size="sm"
                                                iconName="qr-code"
                                                text="Codes"
                                                testID={`registrant-codes-${item.personID}`}
                                                onPress={handleCodes}
                                            />
                                        )}
                                        {canEditEvents && (
                                            <SolidButton
                                                key="remove"
                                                size="sm"
                                                iconName="trash"
                                                type="error"
                                                variant="muted"
                                                text="Remove"
                                                onPress={handleRemoveRegistrant}
                                            />
                                        )}
                                    </View>
                                );

                                // Format subtitle: date + location (if available)
                                const registeredDate = item.registeredAt
                                    ? new Date(item.registeredAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                                    : '';
                                const subtitleParts = [registeredDate, item.locationName].filter(Boolean);
                                const subtitle = subtitleParts.join(' · ');

                                return (
                                    <View style={styles.registrantItemWrapper}>
                                        <ProfileCard
                                            profile={item.Person}
                                            subtitle={subtitle || (item.source === 'webhook' ? 'Registered via form' : 'Manually registered')}
                                            secondarySubtitle={isCheckedIn ? "Checked In" : null}
                                            secondarySubtitleIcon={isCheckedIn ? "checkmark-circle" : null}
                                            buttons={registrantButtons}
                                            onPress={() => setDetailModalRegistrant(item)}
                                            testID={`registrant-card-${item.personID}`}
                                        />
                                    </View>
                                );
                            }}
                            keyboardShouldPersistTaps="handled"
                        />
                    </View>
                );

            case 'map':
                if (!locationWithAddress?.Address?.latitude || !locationWithAddress?.Address?.longitude) {
                    return <EmptyListPlaceholder message="Unable to map this location" />;
                }
                return (
                    <View style={styles.mapContainer}>
                        <MapView
                            key={mapReset}
                            style={styles.map}
                            region={{
                                latitude: locationWithAddress.Address.latitude,
                                longitude: locationWithAddress.Address.longitude,
                                latitudeDelta: 0.02,
                                longitudeDelta: 0.02,
                            }}
                        >
                            <Marker coordinate={{ latitude: locationWithAddress.Address.latitude, longitude: locationWithAddress.Address.longitude }} />
                        </MapView>
                        <View style={styles.mapOverlay}>
                            <TouchableOpacity style={styles.mapButton} onPress={() => setMapReset(Math.random())}>
                                <Text style={styles.mapButtonText}>Reset</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.mapButton}
                                onPress={() => Linking.canOpenURL(google_maps_url).then((canOpen) => Linking.openURL(canOpen ? google_maps_url : apple_maps_url))}
                            >
                                <Text style={styles.mapButtonText}>Directions</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                );

            default:
                return null;
        }
    }, [event, fetchingEvents, fetchingRegistrants, fetchingServiceFlow, registrants, registrantPaths, pendingRegistrants, pendingLinkPerson, resolvePendingMutation, deletePendingMutation, serviceFlowItems, volunteerGroupedTeams, loggedInUser, locationWithAddress, mapReset, EventInfoPanel, effectiveTeamFilter, teamFilterOptions, addRegistrantMutation, removeRegistrantMutation, checkInRegistrantMutation, removeCheckInMutation, canEditEvents, canCreateEvents, canCheckIn, canCreatePaths, filteredAndSortedRegistrants, taskTemplates, applyTemplateMutation, deleteTaskInstanceMutation, registrationInstanceID, isRegistrationExtension, extensionSource, organization?.timezone]);

    // ============ WIDE SCREEN LAYOUT ============
    // Memoize activity routes separately for wide screen to prevent recalculation
    const activityRoutes = useMemo(() => tabRoutes.filter(r => r.key !== 'info'), [tabRoutes]);

    // Clamp tab indices to valid range when routes change
    const safeWideTabIndex = Math.min(wideTabIndex, Math.max(0, activityRoutes.length - 1));
    const safeTabIndex = Math.min(tabIndex, Math.max(0, tabRoutes.length - 1));

    const handleTabIndexChange = useCallback((index) => {
        setTabIndex(index);
        const key = tabRoutes[index]?.key;
        if (key) InteractionManager.runAfterInteractions(() => router.setParams({ tab: key }));
    }, [tabRoutes, router]);

    const handleWideTabIndexChange = useCallback((index) => {
        setWideTabIndex(index);
        const key = activityRoutes[index]?.key;
        if (key) InteractionManager.runAfterInteractions(() => router.setParams({ tab: key }));
    }, [activityRoutes, router]);

    const WideScreenLayout = useMemo(() => (
        <View style={styles.twoColumn}>
            <EventInfoPanel />
            {activityRoutes.length > 0 && (
                <View style={styles.rightPanel}>
                    <TabView
                        navigationState={{ index: safeWideTabIndex, routes: activityRoutes }}
                        renderScene={renderScene}
                        onIndexChange={handleWideTabIndexChange}
                        renderTabBar={renderTabBar}
                        swipeEnabled={isNative}
                        initialLayout={{ width: 500 }}
                        lazy
                    />
                </View>
            )}
        </View>
    ), [activityRoutes, safeWideTabIndex, EventInfoPanel, renderScene, renderTabBar, isNative, handleWideTabIndexChange]);

    // ============ MAIN RENDER ============
    if (isLoading) return <View style={styles.loadingContainer}>{Loaders.eventInfo()}</View>;

    return (
        <View style={styles.container}>
            {isWideScreen ? WideScreenLayout : (
                <TabView
                    navigationState={{ index: safeTabIndex, routes: tabRoutes }}
                    renderScene={renderScene}
                    onIndexChange={handleTabIndexChange}
                    renderTabBar={renderTabBar}
                    swipeEnabled
                    lazy
                />
            )}

            {/* Template Selection Modal */}
            <SheetModal
                isOpen={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                title="Choose a Template"
            >
                <View style={styles.templateList}>
                    {(taskTemplates?.filter(t => t.volunteerTeamID === selectedTemplateTeamID) || []).map(template => (
                        <View key={template.id} style={styles.templateOption}>
                            <Pressable
                                style={styles.templateOptionMain}
                                onPress={() => applyTemplateMutation.mutate({ templateID: template.id, eventInstanceID, currentUser: loggedInUser?.id })}
                            >
                                <Unicon name="clipboard-text-multiple" library='material' size={18} colorVariant="primary" />
                                <View style={styles.templateOptionInfo}>
                                    <Text style={styles.templateOptionName}>{template.name}</Text>
                                    <Text style={styles.templateOptionMeta}>
                                        {template.Members?.length || 0} assignments
                                    </Text>
                                </View>
                            </Pressable>
                            {loggedInUser?.can?.("Update Tasks") && (
                                <Pressable
                                    style={styles.templateOptionEdit}
                                    onPress={() => {
                                        setTemplateModalOpen(false);
                                        router.push({ pathname: '/tasks/templates/create', params: { id: template.id } });
                                    }}
                                >
                                    <Unicon name="create" size={18} colorVariant="textSecondary" />
                                </Pressable>
                            )}
                        </View>
                    ))}
                </View>
            </SheetModal>

            {/* Manage Tasks Modal — add/remove TaskInstances for a team on this event only */}
            <SheetModal
                isOpen={!!manageTasksTeam}
                onClose={() => setManageTasksTeam(null)}
                title="Manage Tasks"
                subtitle={manageTasksTeam?.team}
                helpText={`These changes only affect this event: ${event?.nameOverride || event?.Event?.name || ''}${event?.startTime ? ' — ' + (safeNewDate(event.startTime)?.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: organization?.timezone }) || '') : ''}`}
            >
                <View style={styles.manageTasksList}>
                    {manageTeamTasks.length === 0 ? (
                        <EmptyListPlaceholder message="No tasks for this team" />
                    ) : (
                        manageTeamTasks.map(({ task, instance }) => {
                            const isAdding = createTaskInstanceMutation.isPending && createTaskInstanceMutation.variables?.taskIDs?.some(t => t.id === task.id && t.volunteerTeamID === manageTasksTeam.teamID);
                            const isRemoving = deleteTaskInstanceMutation.isPending && deleteTaskInstanceMutation.variables === instance?.id;
                            return (
                                <View key={task.id} style={styles.manageTaskRow}>
                                    <View style={styles.manageTaskInfo}>
                                        <Text style={styles.manageTaskTitle}>{task.title}</Text>
                                        {task.description ? (
                                            <Text style={styles.manageTaskDesc} numberOfLines={2}>{task.description}</Text>
                                        ) : null}
                                    </View>
                                    {instance ? (
                                        <SolidButton
                                            size="sm"
                                            type="error"
                                            variant="muted"
                                            text="Remove"
                                            iconName="trash-outline"
                                            iconBefore
                                            processing={isRemoving}
                                            testID={`RemoveTask-${task.id}`}
                                            onPress={() => showAlert({
                                                title: "Remove Task",
                                                subtitle: `Remove "${task.title}" from ${event?.nameOverride || event?.Event?.name}?`,
                                                confirmLabel: "Remove",
                                                cancelLabel: "Cancel",
                                                onConfirm: () => deleteTaskInstanceMutation.mutate(instance.id),
                                                onCancel: () => {},
                                            })}
                                        />
                                    ) : (
                                        <SolidButton
                                            size="sm"
                                            type="primary"
                                            text="Add"
                                            iconName="add-outline"
                                            iconBefore
                                            processing={isAdding}
                                            testID={`AddTask-${task.id}`}
                                            onPress={() => addTaskInstanceForTeam(task)}
                                        />
                                    )}
                                </View>
                            );
                        })
                    )}
                </View>
            </SheetModal>

            {/* Add Task Modal — pick any org team, multi-select tasks, batch add to this event only */}
            <SheetModal
                isOpen={addTaskModalOpen}
                onClose={() => setAddTaskModalOpen(false)}
                title="Add Tasks"
                helpText={`Adds tasks to this event only: ${event?.nameOverride || event?.Event?.name || ''}${event?.startTime ? ' — ' + (safeNewDate(event.startTime)?.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: organization?.timezone }) || '') : ''}`}
                confirmText="Add Tasks"
                onConfirm={handleConfirmAddTasks}
                loading={createTaskInstanceMutation.isPending}
            >
                <View style={styles.addTaskList}>
                    <SelectInput
                        title="Team"
                        placeholder="Select a team"
                        value={addTaskSelectedTeamID}
                        options={addTaskTeamOptions}
                        onUpdate={(value) => {
                            setAddTaskSelectedTeamID(value);
                            setAddTaskSelectedTaskIDs([]);
                        }}
                    />
                    {addTaskSelectedTeamID && (
                        addTaskSelectableTasks.length === 0 ? (
                            <EmptyListPlaceholder message="No tasks on this team" />
                        ) : (
                            <View style={styles.addTaskTaskList}>
                                {addTaskSelectableTasks.map(({ task, isExisting }) => {
                                    const isSelected = addTaskSelectedTaskIDs.includes(task.id);
                                    return (
                                        <Pressable
                                            key={task.id}
                                            style={styles.addTaskRow(isExisting)}
                                            onPress={() => {
                                                if (isExisting) return;
                                                setAddTaskSelectedTaskIDs(prev =>
                                                    isSelected
                                                        ? prev.filter(id => id !== task.id)
                                                        : [...prev, task.id]
                                                );
                                            }}
                                            testID={`AddTaskRow-${task.id}`}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                disabled={isExisting}
                                                testID={`AddTaskCheckbox-${task.id}`}
                                            />
                                            <View style={styles.addTaskInfo}>
                                                <Text style={styles.addTaskTitle(isExisting)}>{task.title}</Text>
                                                {task.description ? (
                                                    <Text style={styles.addTaskDesc} numberOfLines={2}>{task.description}</Text>
                                                ) : null}
                                            </View>
                                            {isExisting && (
                                                <View style={styles.addTaskExistingBadge}>
                                                    <Text style={styles.addTaskExistingText}>Added</Text>
                                                </View>
                                            )}
                                        </Pressable>
                                    );
                                })}
                            </View>
                        )
                    )}
                </View>
            </SheetModal>

            {/* Registrant Codes Sheet */}
            <RegistrantCodesSheet
                isOpen={!!codesSheetRegistrant}
                onClose={() => setCodesSheetRegistrant(null)}
                eventInstanceID={registrationInstanceID}
                personID={codesSheetRegistrant?.personID}
                personName={codesSheetRegistrant?.personName}
            />

            {/* Registrant Paths Sheet */}
            <RegistrantPathsSheet
                isOpen={pathsSheetOpen}
                onClose={() => setPathsSheetOpen(false)}
                eventInstanceID={registrationInstanceID}
            />

            {/* Extend Registration Modal */}
            <ExtendRegistrationModal
                isOpen={extendModalOpen}
                onClose={() => setExtendModalOpen(false)}
                eventInstanceID={registrationInstanceID}
                siblingInstances={siblingInstances}
                currentExtensions={registrationExtensions}
            />

            {/* Registrant Detail Modal — opened by tapping a registrant card */}
            <RegistrantDetailModal
                isOpen={!!detailModalRegistrant}
                onClose={() => setDetailModalRegistrant(null)}
                eventInstanceID={registrationInstanceID}
                targetEventInstanceID={isRegistrationExtension ? eventInstanceID : undefined}
                targetLabel={isRegistrationExtension
                    ? `${event?.nameOverride || event?.Event?.name || ''}${event?.startTime ? ` · ${safeNewDate(event.startTime)?.toLocaleString('default', { month: "short", day: "numeric", year: "numeric", timeZone: organization?.timezone }) || ''}` : ''}`
                    : undefined}
                registrant={detailModalRegistrant ? {
                    personID: detailModalRegistrant.personID,
                    firstName: detailModalRegistrant.Person?.firstName,
                    lastName: detailModalRegistrant.Person?.lastName,
                    dateOfBirth: detailModalRegistrant.Person?.dateOfBirth,
                } : null}
                locations={event?.Locations || []}
                registrantPaths={registrantPaths}
                // List isCheckedIn is set-wide (main + extensions). When the
                // viewed instance is part of an extension set the check-in
                // target is THIS instance, so defer to the modal's
                // target-scoped registrantDetail query instead.
                isCheckedIn={(isRegistrationExtension || registrationExtensions.length > 0) ? false : !!detailModalRegistrant?.isCheckedIn}
                onCheckedIn={(personID) => {
                    queryClient.setQueryData(
                        QueryKeys.eventInstances.registrants(registrationInstanceID),
                        (oldData) => oldData ? oldData.map(r =>
                            r.personID === personID ? { ...r, isCheckedIn: true } : r
                        ) : oldData
                    );
                }}
            />

            {/* Registration Email Modal — second step of group registration */}
            <RegistrationEmailModal
                isOpen={!!pendingRegistrationPeople}
                onClose={() => setPendingRegistrationPeople(null)}
                people={pendingRegistrationPeople || []}
                processing={addRegistrantMutation.isPending}
                onRegister={(emailRecipientPersonID) => {
                    if (!pendingRegistrationPeople?.length) return;
                    addRegistrantMutation.mutate({
                        eventInstanceID: registrationInstanceID,
                        personIDs: pendingRegistrationPeople.map(p => p.id),
                        emailRecipientPersonID: emailRecipientPersonID || undefined,
                    });
                }}
            />

        </View>
    );
};


const styles = StyleSheet.create((theme, _rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    loadingContainer: { flex: 1, padding: 20 },
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, minWidth: 380, maxWidth: 420, paddingTop: 20 },
    leftPanelContent: { paddingBottom: 100 },
    rightPanel: { flex: 2 },
    tabContent: { flex: 1 },
    // Full width — the ServiceFlowInstancePanel ScrollView owns the content
    // column width so the scrollbar sits at the screen edge, not the column.
    serviceFlowTabContent: { flex: 1 },
    listPadding: { paddingBottom: 100 },
    taskListPadding: { padding: 16, paddingBottom: 100 },
    taskItemWrapper: { maxWidth: 800, width: '100%', alignSelf: 'center' },

    // Hero
    heroContainer: { height: 180, marginBottom: -60 },
    heroImage: { width: '100%', height: '100%' },
    heroGradient: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 100 },
    gradientEnd: { backgroundColor: theme.colors.background },

    // Header Card
    headerCard: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 20, marginHorizontal: 16, marginBottom: 16 },
    canceledBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.errorContainer, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 12, alignSelf: 'flex-start', gap: 6 },
    canceledText: { color: theme.colors.error, fontSize: 12, fontFamily: theme.fonts.bold },
    eventTitleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    calendarBadge: { width: 52, height: 52, backgroundColor: theme.colors.primaryContainer, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
    calendarBadgeMonth: { fontSize: 10, fontFamily: theme.fonts.bold, color: theme.colors.primary },
    calendarBadgeDay: { fontSize: 22, fontFamily: theme.fonts.bold, color: theme.colors.primary, marginTop: -2 },
    calendarDot: (color) => ({ marginTop:2, width: 8, height: 8, borderRadius: 6, backgroundColor: color }),
    eventTitleInfo: { flex: 1, marginLeft: 14 },
    eventTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    eventSubtitle: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },
    strikethrough: { textDecorationLine: 'line-through', textDecorationStyle: 'solid' },

    // Stats Row
    statsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderTopWidth: 1, borderBottomWidth: 1, borderColor: theme.colors.border, marginBottom: 16 },
    statItem: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border, marginHorizontal: 12 },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase' },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    actionBtnPrimary: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: theme.colors.primary },
    actionBtnPrimaryText: { color: theme.colors.surface, fontSize: 13, fontFamily: theme.fonts.bold },
    actionBtn: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
    actionBtnText: { color: theme.colors.text, fontSize: 13, fontFamily: theme.fonts.bold },

    // Info Section
    infoSection: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 16, marginHorizontal: 16, marginBottom: 16 },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 120 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    infoRowValueSub: { fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right', marginTop: 2 },
    linkText: { color: theme.colors.primary },
    underlineText: { textDecorationLine: 'underline' },

    // Notes Section
    notesSection: { marginHorizontal: 16, marginBottom: 16 },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 8 },
    notesCard: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 16 },
    notesText: { fontSize: 14, color: theme.colors.text, lineHeight: 22 },

    // Links Section
    linksSection: { marginHorizontal: 16, marginBottom: 16 },
    linksGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    linkItem: { marginBottom: 8 },

    // Video Section
    videoSection: { marginHorizontal: 16, marginBottom: 16 },
    videoCard: { borderRadius: 12, overflow: 'hidden', backgroundColor: theme.colors.surface },
    videoThumbnail: { width: '100%', aspectRatio: 16 / 9 },
    playOverlay: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.3)' },
    playButton: { width: 64, height: 64, borderRadius: 32, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center' },

    // Tasks
    addTaskContainer: { alignItems: 'center' },
    categorySection: { marginBottom: 16 },
    categoryTitle: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase' },
    teamHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
    teamHeaderActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    templateChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: theme.colors.primaryContainer,
    },
    templateChipText: {
        fontSize: 12,
        color: theme.colors.primary,
        fontFamily: theme.fonts.heading,
    },
    taskHeaderRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 12, maxWidth: 800, width: '100%', alignSelf: 'center', marginBottom: 12 },
    taskHeaderButtons: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6.5 },
    locationFilterContainer: { flex: 1 },

    // Registrants
    registrantsHeader: { paddingHorizontal: 16, paddingVertical: 12, gap: 10 },
    registrantsTopRow: { flexDirection: 'column', gap: 10, _web: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16 } },
    registrantsButtonsRow: { flexDirection: 'row', gap: 8 },
    registrantsButtonFlex: { flex: 1, _web: { flex: '0 1 auto' } },
    registrantsSearchRow: { flexDirection: 'row', gap: 8, alignItems: 'center', _web: { flex: '0 1 auto' } },
    registrantsSearchFull: { flex: 1, _web: { flex: '0 1 320px' } },
    registrantSortButton: { width: 30, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
    registrantsCountSubtext: { fontSize: 12, color: theme.colors.textSecondary, fontFamily: theme.fonts.body, marginTop: 4, textAlign: 'right' },
    registrantsManagedByText: { fontSize: 12, color: theme.colors.textSecondary, fontFamily: theme.fonts.body, marginTop: 4, flexShrink: 1 },
    registrantsCountRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    extendRegistrationText: { fontSize: 13, color: theme.colors.primary, fontFamily: theme.fonts.bold, marginTop: 4 },
    extensionNote: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: theme.colors.primaryContainer, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 12, alignSelf: 'flex-start' },
    extensionNoteText: { color: theme.colors.primary, fontSize: 12, fontFamily: theme.fonts.body, flexShrink: 1 },
    registrantItemWrapper: { paddingHorizontal: 16 },
    registrantDateGroupHeader: { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 6 },
    registrantDateGroupLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },

    // Pending registrants
    pendingSection: {
        marginHorizontal: 16,
        marginTop: 12,
        marginBottom: 8,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.warning,
        overflow: 'hidden',
    },
    pendingSectionTitle: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.warning,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        paddingHorizontal: 14,
        paddingVertical: 10,
        backgroundColor: theme.colors.warningContainer,
    },
    pendingCard: {
        backgroundColor: theme.colors.surface,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    pendingCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        gap: 12,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    pendingAvatarPlaceholder: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: theme.colors.warningContainer,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
    },
    pendingAvatarInitials: {
        fontSize: 16,
        fontFamily: theme.fonts.bold,
        color: theme.colors.warning,
    },
    pendingInfo: {
        flex: 1,
    },
    pendingName: {
        fontSize: 15,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    pendingMeta: {
        fontSize: 13,
        fontFamily: theme.fonts.body,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    pendingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.warningContainer,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 10,
        gap: 4,
        flexShrink: 0,
    },
    pendingBadgeText: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.warning,
        textTransform: 'uppercase',
    },
    pendingActions: {
        padding: 12,
        gap: 10,
    },
    pendingLinkRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
    },
    pendingDeleteButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors.errorContainer,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
    },
    pendingDeleteIcon: {
        color: theme.colors.error,
    },

    // Map
    mapContainer: { flex: 1 },
    map: { flex: 1 },
    mapOverlay: { position: 'absolute', top: 16, left: 16, right: 16, flexDirection: 'row', justifyContent: 'space-between' },
    mapButton: { backgroundColor: 'rgba(0,0,0,0.7)', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8 },
    mapButtonText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 13 },

    // Instance navigator
    instanceNavigator: { padding: 16, gap: 14, alignItems: 'center' },
    instanceNavigatorTitle: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text, marginBottom: 2 },
    navigatorButtons: { flexDirection: 'row', justifyContent: 'center', gap: 12, width: '100%' },
    calendarWrapper: { width: '100%', maxWidth: 380, alignSelf: 'center' },
    navigatorBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 10, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.surface, flex: 1, justifyContent: 'center' },
    navigatorBtnDisabled: { opacity: 0.4 },
    navigatorBtnText: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },
    navigatorBtnTextDisabled: { color: theme.colors.textSecondary },

    // Template Modal
    templateList: { gap: 8, paddingVertical: 8 },
    templateOption: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        _web:{
            transition: 'background-color 0.2s',
            _hover:{
                backgroundColor: theme.colors.surfaceVariant,
            }
        }
    },
    templateOptionMain: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    templateOptionEdit: {
        padding: 8,
        marginLeft: 8,
        borderRadius: 8,
    },
    templateOptionInfo: { flex: 1 },
    templateOptionName: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text },
    templateOptionMeta: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },

    // Manage Tasks Modal
    manageTasksList: { gap: 8, paddingVertical: 8 },
    manageTaskRow: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        gap: 12,
    },
    manageTaskInfo: { flex: 1 },
    manageTaskTitle: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text },
    manageTaskDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },

    // Add Task Modal
    addTaskList: { gap: 12, paddingVertical: 8 },
    addTaskTaskList: { gap: 8, marginTop: 4 },
    addTaskRow: (isExisting) => ({
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        gap: 12,
        opacity: isExisting ? 0.5 : 1,
        _web: {
            cursor: isExisting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: isExisting ? theme.colors.surface : theme.colors.surfaceVariant,
            },
        },
    }),
    addTaskInfo: { flex: 1 },
    addTaskTitle: (isExisting) => ({
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: isExisting ? theme.colors.textSecondary : theme.colors.text,
    }),
    addTaskDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },
    addTaskExistingBadge: {
        backgroundColor: theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 10,
    },
    addTaskExistingText: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
}));


export default EventInstanceScreen;
